import { rtkApi as api } from '../rtkApi';

export interface Negotiation {
  _id: string;
  contractId: string;
  type: 'counter_offer' | 'amendment' | 'extension' | 'final_acceptance';
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  proposedTerms: any;
  originalTerms: any;
  changes: Array<{
    field: string;
    oldValue: any;
    newValue: any;
    description?: string;
  }>;
  initiatedBy: 'buyer' | 'seller' | 'agent';
  message?: string;
  deadline?: string;
  acceptedBy?: 'buyer' | 'seller' | 'agent';
  acceptedAt?: string;
  rejectedBy?: 'buyer' | 'seller' | 'agent';
  rejectedAt?: string;
  rejectionReason?: string;
  expiresAt?: string;
  attachments?: Array<{
    filename: string;
    originalName: string;
    mimeType: string;
    size: number;
    uploadedBy: string;
    uploadedAt: string;
  }>;
  comments?: Array<{
    author: 'buyer' | 'seller' | 'agent';
    authorName: string;
    comment: string;
    createdAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateNegotiationRequest {
  contractId: string;
  type: 'counter_offer' | 'amendment' | 'extension' | 'final_acceptance';
  proposedTerms: any;
  originalTerms: any;
  changes: Array<{
    field: string;
    oldValue: any;
    newValue: any;
    description?: string;
  }>;
  initiatedBy: 'buyer' | 'seller' | 'agent';
  message?: string;
  deadline?: string;
}

export interface UpdateNegotiationRequest {
  message?: string;
  proposedTerms?: any;
  changes?: Array<{
    field: string;
    oldValue: any;
    newValue: any;
    description?: string;
  }>;
}

export interface AcceptNegotiationRequest {
  acceptedBy: 'buyer' | 'seller' | 'agent';
}

export interface RejectNegotiationRequest {
  rejectedBy: 'buyer' | 'seller' | 'agent';
  rejectionReason?: string;
}

export interface NegotiableFields {
  offerSpecifics: string[];
  contingency: string[];
  timeline: string[];
}

export const negotiationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    createNegotiation: builder.mutation<
      { success: boolean; data: Negotiation; message: string },
      CreateNegotiationRequest
    >({
      query: (negotiationData) => ({
        url: `/negotiation/${negotiationData.contractId}/create`,
        method: 'POST',
        body: negotiationData,
      }),
      invalidatesTags: ['Negotiation', 'Contract'],
    }),
    updateNegotiation: builder.mutation<
      { success: boolean; data: Negotiation; message: string },
      { negotiationId: string; updateData: UpdateNegotiationRequest }
    >({
      query: ({ negotiationId, updateData }) => ({
        url: `/${negotiationId}`,
        method: 'PUT',
        body: updateData,
      }),
      invalidatesTags: ['Negotiation'],
    }),
    acceptNegotiation: builder.mutation<
      { success: boolean; data: Negotiation; message: string },
      { negotiationId: string; acceptData: AcceptNegotiationRequest }
    >({
      query: ({ negotiationId, acceptData }) => ({
        url: `/${negotiationId}/accept`,
        method: 'POST',
        body: acceptData,
      }),
      invalidatesTags: ['Negotiation', 'Contract'],
    }),
    rejectNegotiation: builder.mutation<
      { success: boolean; data: Negotiation; message: string },
      { negotiationId: string; rejectData: RejectNegotiationRequest }
    >({
      query: ({ negotiationId, rejectData }) => ({
        url: `/${negotiationId}/reject`,
        method: 'POST',
        body: rejectData,
      }),
      invalidatesTags: ['Negotiation'],
    }),
    getNegotiationHistory: builder.query<
      { success: boolean; data: Negotiation[] },
      string
    >({
      query: (contractId) => ({
        url: `/negotiation/contract/${contractId}/history`,
        method: 'GET',
      }),
      providesTags: ['Negotiation'],
    }),
    getMostRecentNegotiation: builder.query<
      { success: boolean; data: Negotiation | null },
      string
    >({
      query: (contractId) => ({
        url: `/negotiation/contract/${contractId}/most-recent`,
        method: 'GET',
      }),
      providesTags: ['Negotiation'],
    }),
    getCounterProposal: builder.query<
      { success: boolean; data: Negotiation | null },
      string
    >({
      query: (contractId) => ({
        url: `/negotiation/${contractId}/counter-offer`,
        method: 'GET',
      }),
      providesTags: ['Negotiation'],
    }),
    getNegotiableFields: builder.query<
      { success: boolean; data: NegotiableFields },
      string
    >({
      query: (contractId) => ({
        url: `/negotiation/${contractId}/negotiable-fields`,
        method: 'GET',
      }),
      providesTags: ['Negotiation'],
    }),
    checkExpiredNegotiations: builder.query<
      { success: boolean; message: string; expiredCount: number },
      void
    >({
      query: () => ({
        url: '/negotiation/expired/check',
        method: 'GET',
      }),
      providesTags: ['Negotiation'],
    }),
    getExpiringNegotiations: builder.query<
      { success: boolean; data: any[]; count: number },
      number
    >({
      query: (hours = 24) => ({
        url: `/expiring?hours=${hours}`,
        method: 'GET',
      }),
      providesTags: ['Negotiation'],
    }),
    getNegotiationTemplates: builder.query<
      { success: boolean; data: any[]; count: number },
      void
    >({
      query: () => ({
        url: '/negotiation/templates',
        method: 'GET',
      }),
      providesTags: ['Negotiation'],
    }),
    getNegotiationTemplate: builder.query<
      { success: boolean; data: any },
      string
    >({
      query: (templateId) => ({
        url: `/templates/${templateId}`,
        method: 'GET',
      }),
      providesTags: ['Negotiation'],
    }),
    applyNegotiationTemplate: builder.mutation<
      { success: boolean; data: any; message: string },
      { contractId: string; templateId: string; customData?: any }
    >({
      query: ({ contractId, templateId, customData }) => ({
        url: `/${contractId}/apply-template`,
        method: 'POST',
        body: { templateId, customData },
      }),
      invalidatesTags: ['Negotiation', 'Contract'],
    }),
  }),
  overrideExisting: true,
});

export const {
  useCreateNegotiationMutation,
  useUpdateNegotiationMutation,
  useAcceptNegotiationMutation: useAcceptNegotiationTermsMutation,
  useRejectNegotiationMutation: useRejectNegotiationTermsMutation,
  useGetNegotiationHistoryQuery,
  useGetMostRecentNegotiationQuery,
  useGetCounterProposalQuery,
  useGetNegotiableFieldsQuery,
  useCheckExpiredNegotiationsQuery,
  useGetExpiringNegotiationsQuery,
  useGetNegotiationTemplatesQuery,
  useGetNegotiationTemplateQuery,
  useApplyNegotiationTemplateMutation,
} = negotiationApi;

// Types are already exported above as interfaces
